import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getScheduledReports, createScheduledReport, deleteScheduledReport, getScheduledReportLogs, getUsers, downloadRiskRegisterPDF, downloadSPIPDF, downloadCompliancePDF } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Clock, Plus, Trash2, Calendar, Mail, FileText, RefreshCw,
  CheckCircle2, AlertCircle, Users, Send, History, Eye, Download, MoreHorizontal, Play
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const REPORT_TYPES = [
  { value: 'risk_register', label: 'Risk Register Report' },
  { value: 'incident_summary', label: 'Incident Summary Report' },
  { value: 'capa_status', label: 'CAPA Status Report' },
  { value: 'audit_summary', label: 'Audit Summary Report' },
  { value: 'compliance_gaca', label: 'GACA Compliance Report' },
  { value: 'compliance_easa', label: 'EASA Compliance Report' },
  { value: 'spi_dashboard', label: 'SPI Dashboard Report' },
  { value: 'training_completion', label: 'Training Completion Report' }
];

const EXPORT_FORMATS = [
  { value: 'pdf', label: 'PDF Document', icon: '📄' },
  { value: 'xml', label: 'XML Format', icon: '📋' },
  { value: 'eccairs2', label: 'ECCAIRS 2 (EU/EASA)', icon: '🇪🇺' },
  { value: 'json', label: 'JSON Data', icon: '{ }' }
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' }
];

export default function ScheduledReportsPage() {
  const { t } = useTranslation();
  const { api, token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('schedules');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    report_type: '',
    frequency: 'weekly',
    export_format: 'pdf',
    hour: '8',
    day_of_week: 'mon',
    day_of_month: '1',
    recipients: []
  });

  useEffect(() => {
    if (api) {
      fetchData();
    }
  }, [api]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reportsRes, logsRes, usersRes] = await Promise.all([
        getScheduledReports(api),
        getScheduledReportLogs(api, 20),
        getUsers(api)
      ]);
      setReports(reportsRes.data.reports || []);
      setLogs(logsRes.data.logs || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch scheduled reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.report_type || formData.recipients.length === 0) {
      return;
    }
    
    try {
      setCreating(true);
      await createScheduledReport(api, {
        report_type: formData.report_type,
        frequency: formData.frequency,
        export_format: formData.export_format,
        recipients: formData.recipients,
        hour: parseInt(formData.hour),
        day_of_week: formData.day_of_week,
        day_of_month: parseInt(formData.day_of_month)
      });
      setShowCreateDialog(false);
      setFormData({
        report_type: '',
        frequency: 'weekly',
        export_format: 'pdf',
        hour: '8',
        day_of_week: 'mon',
        day_of_month: '1',
        recipients: []
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create scheduled report:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!confirm('Are you sure you want to delete this scheduled report?')) {
      return;
    }
    
    try {
      await deleteScheduledReport(api, reportId);
      fetchData();
    } catch (error) {
      console.error('Failed to delete scheduled report:', error);
    }
  };

  const toggleRecipient = (userId) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.includes(userId)
        ? prev.recipients.filter(id => id !== userId)
        : [...prev.recipients, userId]
    }));
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowViewDialog(true);
  };

  const handleExportNow = async (report) => {
    setExporting(report.id);
    try {
      let response;
      let filename;
      const dateStr = format(new Date(), 'yyyyMMdd');
      
      // Map report type to download function
      switch (report.report_type) {
        case 'risk_register':
          response = await downloadRiskRegisterPDF(token);
          filename = `risk_register_${dateStr}.pdf`;
          break;
        case 'spi_dashboard':
          response = await downloadSPIPDF(token);
          filename = `spi_dashboard_${dateStr}.pdf`;
          break;
        case 'compliance_gaca':
          response = await downloadCompliancePDF(token, 'gaca');
          filename = `gaca_compliance_${dateStr}.pdf`;
          break;
        case 'compliance_easa':
          response = await downloadCompliancePDF(token, 'easa');
          filename = `easa_compliance_${dateStr}.pdf`;
          break;
        default:
          response = await downloadRiskRegisterPDF(token);
          filename = `${report.report_type}_${dateStr}.pdf`;
      }
      
      if (!response || !response.data) {
        throw new Error('No data received from server');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Failed to export report:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(null);
    }
  };

  const getFrequencyLabel = (report) => {
    switch (report.frequency) {
      case 'daily':
        return `Daily at ${report.hour}:00`;
      case 'weekly':
        const dayLabel = DAYS_OF_WEEK.find(d => d.value === report.day_of_week)?.label || report.day_of_week;
        return `Every ${dayLabel} at ${report.hour}:00`;
      case 'monthly':
        return `Monthly on day ${report.day_of_month} at ${report.hour}:00`;
      default:
        return report.frequency;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="scheduled-reports-title">
            Scheduled Reports
          </h1>
          <p className="text-slate-400 mt-1">Automate report generation and delivery</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="create-schedule-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Report
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">Create Scheduled Report</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Report Type */}
                <div>
                  <Label className="text-slate-300">Report Type</Label>
                  <Select 
                    value={formData.report_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, report_type: v }))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {REPORT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Export Format */}
                <div>
                  <Label className="text-slate-300">Export Format</Label>
                  <Select 
                    value={formData.export_format} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, export_format: v }))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {EXPORT_FORMATS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          <span className="flex items-center gap-2">
                            <span>{f.icon}</span>
                            <span>{f.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-slate-500 text-xs mt-1">
                    {formData.export_format === 'eccairs2' && 'EU/EASA compliant format for regulatory submission'}
                    {formData.export_format === 'pdf' && 'Professional PDF document for presentations'}
                    {formData.export_format === 'xml' && 'Standard XML for system integration'}
                    {formData.export_format === 'json' && 'JSON data for technical analysis'}
                  </p>
                </div>

                {/* Frequency */}
                <div>
                  <Label className="text-slate-300">Frequency</Label>
                  <Select 
                    value={formData.frequency} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {FREQUENCIES.map((f) => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Day of Week (for weekly) */}
                {formData.frequency === 'weekly' && (
                  <div>
                    <Label className="text-slate-300">Day of Week</Label>
                    <Select 
                      value={formData.day_of_week} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, day_of_week: v }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {DAYS_OF_WEEK.map((d) => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Day of Month (for monthly) */}
                {formData.frequency === 'monthly' && (
                  <div>
                    <Label className="text-slate-300">Day of Month</Label>
                    <Input
                      type="number"
                      min="1"
                      max="28"
                      value={formData.day_of_month}
                      onChange={(e) => setFormData(prev => ({ ...prev, day_of_month: e.target.value }))}
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>
                )}

                {/* Hour */}
                <div>
                  <Label className="text-slate-300">Time (Hour, 24h format)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={formData.hour}
                    onChange={(e) => setFormData(prev => ({ ...prev, hour: e.target.value }))}
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>

                {/* Recipients */}
                <div>
                  <Label className="text-slate-300">Recipients</Label>
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-2 p-2 bg-slate-800 rounded-lg border border-slate-700">
                    {users.map((u) => (
                      <label 
                        key={u.id} 
                        className="flex items-center gap-2 cursor-pointer hover:bg-slate-700/50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.recipients.includes(u.id)}
                          onChange={() => toggleRecipient(u.id)}
                          className="rounded border-slate-600"
                        />
                        <span className="text-slate-300 text-sm">{u.name}</span>
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400 ml-auto">
                          {u.role}
                        </Badge>
                      </label>
                    ))}
                  </div>
                  <p className="text-slate-500 text-xs mt-1">
                    {formData.recipients.length} recipient(s) selected
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  className="border-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating || !formData.report_type || formData.recipients.length === 0}
                  className="bg-sky-600 hover:bg-sky-700"
                >
                  {creating ? 'Creating...' : 'Create Schedule'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-900/60 border border-slate-800 p-1">
          <TabsTrigger 
            value="schedules" 
            className="data-[state=active]:bg-slate-700"
            data-testid="tab-schedules"
          >
            <Clock className="w-4 h-4 mr-2" />
            Schedules ({reports.length})
          </TabsTrigger>
          <TabsTrigger 
            value="history"
            className="data-[state=active]:bg-slate-700"
            data-testid="tab-history"
          >
            <History className="w-4 h-4 mr-2" />
            History ({logs.length})
          </TabsTrigger>
        </TabsList>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="mt-6">
          {reports.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-12 text-center">
                <Clock className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Scheduled Reports</h3>
                <p className="text-slate-400 mb-4">Create your first scheduled report to automate report delivery.</p>
                <Button onClick={() => setShowCreateDialog(true)} className="bg-sky-600 hover:bg-sky-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Report
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reports.map((report) => {
                const reportType = REPORT_TYPES.find(r => r.value === report.report_type);
                const exportFormat = EXPORT_FORMATS.find(f => f.value === report.export_format);
                return (
                  <Card 
                    key={report.id} 
                    className="bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-colors"
                    data-testid={`schedule-card-${report.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-sky-600/20 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-sky-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{reportType?.label || report.report_type}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-3 h-3 text-slate-500" />
                              <span className="text-slate-400 text-sm">{getFrequencyLabel(report)}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Users className="w-3 h-3 text-slate-500" />
                              <span className="text-slate-400 text-sm">{report.recipients?.length || 0} recipients</span>
                            </div>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white" data-testid={`schedule-actions-${report.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                            <DropdownMenuItem onClick={() => handleViewReport(report)} className="cursor-pointer">
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleExportNow(report)} 
                              disabled={exporting === report.id}
                              className="cursor-pointer"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              {exporting === report.id ? 'Exporting...' : 'Export Now (PDF)'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-700" />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(report.id)} 
                              className="cursor-pointer text-red-400 focus:text-red-400"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Schedule
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge 
                            className={cn(
                              report.is_active 
                                ? "bg-emerald-600/30 text-emerald-300 border-emerald-500/50" 
                                : "bg-slate-600/30 text-slate-300 border-slate-500/50"
                            )}
                          >
                            {report.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className="border-slate-600 text-slate-300"
                          >
                            {exportFormat?.icon} {(report.export_format || 'pdf').toUpperCase()}
                          </Badge>
                        </div>
                        <span className="text-slate-500 text-xs">
                          Created {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          {logs.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-12 text-center">
                <History className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Report History</h3>
                <p className="text-slate-400">Report generation history will appear here.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-0">
                <div className="divide-y divide-slate-800">
                  {logs.map((log) => {
                    const reportType = REPORT_TYPES.find(r => r.value === log.report_type);
                    return (
                      <div key={log.id} className="p-4 hover:bg-slate-800/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-emerald-600/20 flex items-center justify-center">
                              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{reportType?.label || log.report_type}</p>
                              <p className="text-slate-400 text-sm">
                                Sent to {log.recipients?.length || 0} recipients
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-slate-400 text-sm">
                              {format(new Date(log.generated_at), 'MMM d, yyyy HH:mm')}
                            </p>
                            <Badge className="bg-emerald-600/30 text-emerald-300 text-xs">
                              {log.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* View Schedule Details Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="h-5 w-5 text-sky-400" />
              Schedule Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedReport && (() => {
            const reportType = REPORT_TYPES.find(r => r.value === selectedReport.report_type);
            const exportFormat = EXPORT_FORMATS.find(f => f.value === selectedReport.export_format);
            const selectedRecipients = users.filter(u => selectedReport.recipients?.includes(u.id));
            
            return (
              <div className="space-y-4 py-2">
                {/* Report Type */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-sky-600/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-sky-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">{reportType?.label || selectedReport.report_type}</h3>
                      <p className="text-slate-400 text-sm">{getFrequencyLabel(selectedReport)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Export Format */}
                <div>
                  <Label className="text-slate-400 text-sm">Export Format</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant="outline" className="border-slate-600 text-slate-300">
                      {exportFormat?.icon} {exportFormat?.label || selectedReport.export_format?.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                
                {/* Status */}
                <div>
                  <Label className="text-slate-400 text-sm">Status</Label>
                  <div className="mt-1">
                    <Badge 
                      className={cn(
                        selectedReport.is_active 
                          ? "bg-emerald-600/30 text-emerald-300 border-emerald-500/50" 
                          : "bg-slate-600/30 text-slate-300 border-slate-500/50"
                      )}
                    >
                      {selectedReport.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                
                {/* Recipients */}
                <div>
                  <Label className="text-slate-400 text-sm">Recipients ({selectedRecipients.length})</Label>
                  <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {selectedRecipients.length > 0 ? (
                      selectedRecipients.map(u => (
                        <div key={u.id} className="flex items-center gap-2 p-2 bg-slate-800/50 rounded">
                          <Mail className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-300 text-sm">{u.name}</span>
                          <Badge variant="outline" className="text-xs border-slate-600 text-slate-400 ml-auto">
                            {u.role}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-sm">No recipients configured</p>
                    )}
                  </div>
                </div>
                
                {/* Created Date */}
                <div className="text-sm text-slate-500">
                  Created {format(new Date(selectedReport.created_at), 'MMM d, yyyy HH:mm')}
                </div>
              </div>
            );
          })()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)} className="border-slate-700">
              Close
            </Button>
            <Button 
              onClick={() => { handleExportNow(selectedReport); setShowViewDialog(false); }}
              disabled={exporting === selectedReport?.id}
              className="bg-sky-600 hover:bg-sky-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
